// Runtime-safe HTML sanitizer.
//
// isomorphic-dompurify pulls in jsdom, which does not work in the Cloudflare
// Workers runtime used for SSR (it throws "Cannot read properties of undefined
// (reading 'bind')" while streaming the page). This sanitizer is pure string
// work, so it behaves identically on the server and in the browser.
//
// It is applied both when blog content is saved and when it is rendered, so a
// compromised or careless editor account cannot ship JavaScript to readers.

const DANGEROUS_TAGS = [
  "script",
  "style",
  "iframe",
  "frame",
  "frameset",
  "object",
  "embed",
  "applet",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "option",
  "link",
  "meta",
  "base",
  "svg",
  "math",
  "template",
  "portal",
  "noscript",
];

// Attributes that can execute script or redirect submissions.
const DANGEROUS_ATTRS = [
  "srcdoc",
  "formaction",
  "action",
  "background",
  "dynsrc",
  "lowsrc",
  "ping",
];

const URL_ATTRS = ["href", "src", "xlink:href", "data", "poster", "srcset"];

/** Decodes entities/whitespace tricks so "java&#115;cript:" is caught too. */
function normalizeUrl(value: string): string {
  return value
    .replace(/&#x?([0-9a-f]+);?/gi, (_m, code: string) =>
      String.fromCharCode(parseInt(code, /^x/i.test(_m.slice(2, 3)) ? 16 : 10)),
    )
    .replace(/&#(\d+);?/g, (_m, code: string) => String.fromCharCode(Number(code)))
    .replace(/[\s\u0000-\u001f]/g, "")
    .toLowerCase();
}

function isDangerousUrl(value: string): boolean {
  const v = normalizeUrl(value);
  return (
    v.startsWith("javascript:") ||
    v.startsWith("vbscript:") ||
    (v.startsWith("data:") && !v.startsWith("data:image/"))
  );
}

export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return "";
  let out = html;

  // Strip comments (can hide conditional-comment script in old engines).
  out = out.replace(/<!--[\s\S]*?-->/g, "");

  // Drop dangerous elements together with their content.
  for (const tag of DANGEROUS_TAGS) {
    out = out.replace(
      new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?<\\/${tag}\\s*>`, "gi"),
      "",
    );
    // Unclosed / self-closing variants.
    out = out.replace(new RegExp(`<\\/?${tag}\\b[^>]*>`, "gi"), "");
  }

  // Remove inline event handlers: onclick="..." / onclick='...' / onclick=...
  out = out.replace(/\son[a-z-]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");

  // Remove other script-capable attributes.
  for (const attr of DANGEROUS_ATTRS) {
    out = out.replace(
      new RegExp(`\\s${attr}\\s*=\\s*(?:"[^"]*"|'[^']*'|[^\\s>]+)`, "gi"),
      "",
    );
  }

  // Keep ordinary inline styling (colors, alignment, table/image sizing) but
  // drop any style attribute carrying script-capable CSS.
  out = out.replace(/\sstyle\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, (m, v: string) =>
    /expression\s*\(|javascript:|vbscript:|behavior\s*:|-moz-binding|@import/i.test(
      v.replace(/\\|\/\*.*?\*\/|\s+/g, ""),
    )
      ? ""
      : m,
  );

  // Neutralize javascript:/vbscript:/non-image data: URLs in link attributes.
  for (const attr of URL_ATTRS) {
    const pattern = new RegExp(
      `\\s${attr.replace(":", "\\:")}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`,
      "gi",
    );
    out = out.replace(pattern, (match, dq?: string, sq?: string, bare?: string) => {
      const value = dq ?? sq ?? bare ?? "";
      return isDangerousUrl(value) ? "" : match;
    });
  }

  return out;
}
